import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient } from '@/lib/api/client'
import {
  ArtworkPreview,
  humanize,
  StudioKpi,
  studioButton,
} from '@/features/films/StudioComponents'

type Approval = { id: string; verdict: string; finding: string; reviewedAt: string }
type World = {
  artistId: string
  artworkId: string
  experienceId: string
  revision: number
  requestId: string
  name: string
  status: string
  reasonCodes: string[]
  filmApproval: Approval | null
}
type Wave = {
  wave: number
  status: string
  bundleManifestSha256?: string
  reviewUrl?: string
  reason?: string
  critique?: {
    verdict: string
    reason: string
    corrections: string[]
    gates: Record<string, boolean>
  }
}
type Frame = { wave: number; name: string; url: string; sha256: string }
type Detail = World & {
  evidence?: {
    result?: { selectedWave?: number; waves?: Wave[]; costs?: unknown }
    frames?: Frame[]
  }
  approvalHistory: Approval[]
  filmHandoff?: { jobId: string }
}
const base = '/api/v1/worlds'
const key = (w: World) =>
  [w.artistId, w.artworkId, w.experienceId].map(encodeURIComponent).join('/')
const button = studioButton

export function WorldStudioPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [params] = useSearchParams()
  const worldParam = params.get('world') || ''
  const qc = useQueryClient()
  const [selected, setSelected] = useState('')
  useEffect(() => {
    if (/^\d+\/\d+\/[a-zA-Z0-9_-]{1,128}$/.test(worldParam)) setSelected(worldParam)
  }, [worldParam])
  const [waveNumber, setWaveNumber] = useState(0)
  const [checked, setChecked] = useState<string[]>([])
  const [finding, setFinding] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [pages, setPages] = useState<World[]>([])
  const list = useQuery({
    queryKey: ['worlds'],
    queryFn: async () =>
      (await apiClient.get<{ worlds: World[]; nextCursor: string | null }>(base)).data,
    refetchInterval: 30000,
  })
  const [cursor, setCursor] = useState<string | null>(null)
  const initialized = useRef(false)
  useEffect(() => {
    if (!list.data) return
    // Poll the first page without discarding pages explicitly scanned by a reviewer.
    setPages((old) => [
      ...list.data.worlds,
      ...old.filter((w) => !list.data.worlds.some((first) => key(first) === key(w))),
    ])
    if (!initialized.current) {
      setCursor(list.data.nextCursor)
      initialized.current = true
    }
  }, [list.data])
  const detail = useQuery({
    queryKey: ['world', selected],
    queryFn: async () => (await apiClient.get<Detail>(`${base}/${selected}`)).data,
    enabled: !!selected,
    refetchInterval: 60000,
  })
  const world = detail.data
  const creations = useQuery({
    queryKey: ['world-creations'],
    queryFn: async () =>
      (
        await apiClient.get<{
          enabled: boolean
          creations: {
            id: string
            title: string
            artist_name?: string
            source_image_url?: string
            stage: string
            error?: string
            state: {
              scope?: { artistId: string; artworkId: string; experienceId: string }
              filmJobId?: string
            }
          }[]
        }>(`${base}/creations`)
      ).data,
    refetchInterval: 30000,
    retry: false,
  })
  const overview = useQuery({
    queryKey: ['world-overview'],
    queryFn: async () =>
      (
        await apiClient.get<{
          stages: { stage: string; count: number }[]
          awaitingSource: number
          candidates: {
            id: string
            programme_id: string
            title: string
            artist_name: string
            source_image_url: string
          }[]
        }>(`${base}/overview`)
      ).data,
    refetchInterval: 30000,
    retry: false,
  })
  useEffect(() => {
    setWaveNumber(0)
    setChecked([])
    setFinding('')
    setMessage('')
  }, [selected])
  const waves = world?.evidence?.result?.waves || []
  const wave =
    waves.find((w) => w.wave === waveNumber) ||
    waves.find((w) => w.wave === world?.evidence?.result?.selectedWave) ||
    waves.at(-1)
  const frames = (world?.evidence?.frames || []).filter((f) => f.wave === wave?.wave)
  const awaiting =
    !!world && ['AWAITING_SPATIAL_APPROVAL', 'NEEDS_SPATIAL_REVISION'].includes(world.status)
  const canApprove =
    awaiting && wave?.status === 'KEEP' && world?.evidence?.result?.selectedWave === wave.wave
  async function action(fn: () => Promise<void>) {
    setBusy(true)
    setMessage('')
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['worlds'] })
      await qc.invalidateQueries({ queryKey: ['world', selected] })
      await qc.invalidateQueries({ queryKey: ['content-studio'] })
      await qc.invalidateQueries({ queryKey: ['world-creations'] })
    } catch (e) {
      const error = e as { response?: { data?: { error?: string } }; message?: string }
      setMessage(error.response?.data?.error || error.message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }
  async function review(verdict: string) {
    if (!world || !wave) return
    await apiClient.post(`${base}/${selected}/review`, {
      expectedRevision: world.revision,
      requestId: world.requestId,
      wave: wave.wave,
      bundleManifestSha256: wave.bundleManifestSha256,
      verdict,
      finding,
      frameNames: checked,
    })
    setMessage(
      verdict === 'approve_for_film'
        ? 'Approved for film use only. Customer scene and social posting remain unchanged.'
        : 'Correction recorded; no paid retry started.'
    )
    setChecked([])
  }
  const Container = embedded ? 'section' : 'main'
  return (
    <Container className={embedded ? 'space-y-5' : 'mx-auto max-w-7xl space-y-5 p-4 md:p-8'}>
      {embedded && (
        <div>
          <h2 className="text-xl font-semibold">World review</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Inspect the interactive scene, original pixels and mobile motion evidence. Approving a
            world lets the active runner continue to film planning and rendering; it does not
            publish anything.
          </p>
        </div>
      )}
      {(message || list.error || detail.error) && embedded && (
        <p role="status" className="rounded-xl border p-4 text-sm">
          {message || 'World evidence unavailable. Retry before reviewing.'}
        </p>
      )}
      {!embedded && (
        <>
          <header className="flex flex-wrap justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Arthaus · Scene production
              </p>
              <h1 className="mt-1 text-3xl font-semibold">World Studio</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Build and screen immersive scenes before they become films.
              </p>
            </div>
            <Link className={button} to="/app/films?view=candidates">
              Review staged artworks →
            </Link>
          </header>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StudioKpi
              label="Awaiting source review"
              value={overview.data?.awaitingSource}
              note="Staged artworks, not built worlds"
            />
            <StudioKpi
              label="Creation pipelines"
              value={overview.data?.stages?.reduce((n, s) => n + s.count, 0)}
              note="All time"
            />
            <StudioKpi
              label="Scenes loaded"
              value={pages.length}
              note="This scan; not all-time totals"
            />
            <StudioKpi
              label="Ready for screening"
              value={
                pages.filter((w) =>
                  ['AWAITING_SPATIAL_APPROVAL', 'NEEDS_SPATIAL_REVISION'].includes(w.status)
                ).length
              }
              note="Within loaded scenes"
            />
          </div>
          {(message || list.error || detail.error || creations.error || overview.error) && (
            <p role="status" className="rounded border p-3">
              {message ||
                'Some World Studio data is unavailable. Refresh or check service configuration.'}
            </p>
          )}
          <section className="space-y-3 rounded-xl border p-4">
            <h2 className="text-lg font-medium">How a world becomes a film</h2>
            <ol className="grid gap-3 text-sm sm:grid-cols-3">
              <li className="rounded bg-muted p-3">
                <strong>1. Review the artwork</strong>
                <p className="mt-1 text-muted-foreground">
                  Choose a discovered candidate, verify its original, and approve a
                  production budget in Film Studio.
                </p>
              </li>
              <li className="rounded bg-muted p-3">
                <strong>2. Build and screen the world</strong>
                <p className="mt-1 text-muted-foreground">
                  The runner builds layers and motion. Inspect source fidelity, phone framing and
                  structural evidence here.
                </p>
              </li>
              <li className="rounded bg-muted p-3">
                <strong>3. Direct and review the film</strong>
                <p className="mt-1 text-muted-foreground">
                  World approval permits film use only. The encoded film needs its own critique and
                  human review.
                </p>
              </li>
            </ol>
            <p className="rounded border p-3 text-sm">
              {creations.isLoading
                ? 'Checking runner availability…'
                : creations.error
                  ? 'Runner availability could not be checked.'
                  : creations.data?.enabled
                    ? 'Creation runner enabled. Only source- and budget-approved candidates can advance.'
                    : 'Paid world creation is held. Staged artworks remain available for source review; production requires runner activation and an accepted spend policy.'}{' '}
              Customer scene promotion and Instagram posting are separate.
            </p>
          </section>
          {!!overview.data?.candidates?.length && (
            <section className="space-y-3">
              <div className="flex flex-wrap justify-between gap-2">
                <h2 className="text-xl font-medium">Staged artwork preview</h2>
                <Link className="text-sm underline" to="/app/films?view=candidates">
                  Review all {overview.data.awaitingSource} candidates →
                </Link>
              </div>
              <p className="text-xs text-muted-foreground">
                Latest six candidates. These are source artworks waiting for approval—not finished
                immersive scenes.
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {overview.data.candidates.map((c) => (
                  <Link
                    key={c.id}
                    className="min-w-0 space-y-2 rounded-xl border p-3 hover:bg-muted"
                    to={`/app/films?view=candidates&programme=${encodeURIComponent(c.programme_id)}`}
                  >
                    <ArtworkPreview title={c.title} sourceUrl={c.source_image_url} />
                    <strong className="block">{c.title}</strong>
                    <p className="text-sm text-muted-foreground">
                      {c.artist_name} · Awaiting source review
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
          <section className="space-y-3 rounded-xl border p-4">
            <h2 className="text-xl font-medium">Creation pipelines</h2>
            <p className="text-xs text-muted-foreground">
              Latest 50 pipelines, tracked independently of the film render queue.
            </p>
            {!creations.isLoading && !creations.error && !creations.data?.creations.length && (
              <p className="text-sm text-muted-foreground">
                No creation pipelines have started yet. Begin with a staged artwork; approving a
                source does not bypass the production budget gate.
              </p>
            )}
            {creations.data?.creations.map((c) => (
              <div key={c.id} className="grid gap-3 border-t py-3 sm:grid-cols-[120px_1fr]">
                <ArtworkPreview title={c.title} sourceUrl={c.source_image_url} />
                <div className="min-w-0 space-y-2">
                  <strong>{c.title}</strong>
                  <p className="text-sm">
                    {c.artist_name} · {humanize(c.stage)}
                  </p>
                  {c.error && <p className="break-words text-sm text-red-600">{c.error}</p>}
                  <div className="flex flex-wrap gap-2">
                    {c.state.scope && (
                      <button
                        className={button}
                        onClick={() => setSelected(key({ ...c.state.scope } as World))}
                      >
                        Screen world
                      </button>
                    )}
                    {c.state.filmJobId && (
                      <Link
                        className={button}
                        to={`/app/films?job=${encodeURIComponent(c.state.filmJobId)}`}
                      >
                        Review film
                      </Link>
                    )}
                    {!['release_review', 'cancelled', 'failed'].includes(c.stage) && (
                      <button
                        className={button}
                        disabled={busy}
                        onClick={() =>
                          action(async () => {
                            await apiClient.post(`${base}/creations/${c.id}/cancel`)
                            await creations.refetch()
                          })
                        }
                      >
                        Cancel pipeline
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </section>
        </>
      )}
      <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
        <section className="space-y-2 rounded border p-4">
          <h2 className="text-xl">Scene screening queue</h2>
          {!pages.length && !list.isLoading && (
            <p className="text-sm text-muted-foreground">
              No managed layered scenes in this scan. Legacy panoramas and research demos are not
              relabelled as approved worlds.
              {cursor
                ? ' Scan the next page to continue.'
                : ' New builds will appear here with evidence when the runner advances.'}
            </p>
          )}
          {pages.map((w) => (
            <button
              key={key(w)}
              className={`${button} w-full break-words text-left ${selected === key(w) ? 'bg-muted' : ''}`}
              onClick={() => setSelected(key(w))}
            >
              <strong>{w.name}</strong>
              <p className="text-sm">{humanize(w.status)}</p>
              <p className="text-xs">
                Artwork {w.artworkId} · {w.experienceId}
              </p>
              {w.filmApproval && <p className="text-xs">Approved for film use</p>}
            </button>
          ))}
          {cursor && (
            <button
              className={button}
              disabled={busy}
              onClick={() =>
                action(async () => {
                  const { data } = await apiClient.get<{
                    worlds: World[]
                    nextCursor: string | null
                  }>(base, { params: { cursor } })
                  setPages((old) => [
                    ...old,
                    ...data.worlds.filter((w) => !old.some((existing) => key(existing) === key(w))),
                  ])
                  setCursor(data.nextCursor)
                })
              }
            >
              Scan next page
            </button>
          )}
        </section>
        <section className="space-y-4 rounded border p-4">
          {world ? (
            <>
              <h2 className="text-xl">{world.name}</h2>
              <p className="break-words">
                {world.status} · revision {world.revision}
              </p>
              <p className="text-xs">{world.reasonCodes.join(' · ')}</p>
              {waves.length > 0 && (
                <select
                  aria-label="Candidate wave"
                  className="rounded border bg-background p-2"
                  value={wave?.wave || 0}
                  onChange={(e) => {
                    setWaveNumber(Number(e.target.value))
                    setChecked([])
                  }}
                >
                  {waves.map((w) => (
                    <option key={w.wave} value={w.wave}>
                      Wave {w.wave} · {w.status}
                    </option>
                  ))}
                </select>
              )}
              {wave?.reviewUrl && (
                <a
                  className="inline-block rounded border p-3 underline"
                  href={wave.reviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open interactive candidate · Debug / Surfaces / Structure
                </a>
              )}
              <p className="text-sm">
                Evidence shows actual source, camera bounds, motion phases and structural ownership.
                Private screenshot links expire; Refresh evidence renews them. A rejected candidate
                may have frames without an interactive public URL.
              </p>
              <button
                className={button}
                disabled={busy}
                onClick={() => {
                  void detail.refetch()
                }}
              >
                Refresh evidence
              </button>
              {(wave?.critique || wave?.reason) && (
                <div className="rounded border p-3">
                  <h3>Independent critique: {wave.status}</h3>
                  <p>{wave.critique?.reason || wave.reason}</p>
                  <ul className="list-disc pl-5">
                    {wave.critique?.corrections.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                  <p className="text-xs">
                    {Object.entries(wave.critique?.gates || {})
                      .map(([k, v]) => `${k}: ${v ? 'pass' : 'fail'}`)
                      .join(' · ')}
                  </p>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {frames.map((f) => (
                  <label key={`${f.wave}-${f.name}`} className="space-y-2 rounded border p-2">
                    <a href={f.url} target="_blank" rel="noopener noreferrer">
                      <img
                        loading="lazy"
                        className="max-h-96 w-full object-contain"
                        src={f.url}
                        alt={f.name}
                      />
                    </a>
                    <span className="block text-xs">
                      <input
                        type="checkbox"
                        checked={checked.includes(f.name)}
                        onChange={(e) =>
                          setChecked((old) =>
                            e.target.checked ? [...old, f.name] : old.filter((n) => n !== f.name)
                          )
                        }
                      />{' '}
                      Reviewed {f.name}
                    </span>
                  </label>
                ))}
              </div>
              {awaiting && wave?.bundleManifestSha256 && (
                <div className="space-y-3 border-t pt-4">
                  <h3>Review this exact candidate</h3>
                  <textarea
                    aria-label="Scene review finding"
                    className="min-h-24 w-full rounded border bg-background p-2"
                    value={finding}
                    onChange={(e) => setFinding(e.target.value)}
                    placeholder="What works or needs correction? Review source fidelity, framing, depth, seams and motion."
                  />
                  <p className="text-xs">
                    Approval requires canonical mobile framing and a mobile motion frame. A
                    rejected/incomplete model result cannot be approved through this screen.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={button}
                      disabled={
                        busy ||
                        !canApprove ||
                        !finding.trim() ||
                        !checked.includes('mobile-canonical.png') ||
                        !checked.some((n) => /^mobile-motion-/.test(n))
                      }
                      onClick={() => action(() => review('approve_for_film'))}
                    >
                      Approve for film use
                    </button>
                    <button
                      className={button}
                      disabled={busy || checked.length < 2 || !finding.trim()}
                      onClick={() => action(() => review('revise'))}
                    >
                      Request correction / revoke film approval
                    </button>
                  </div>
                </div>
              )}
              {!['CANCELLED', 'FAILED', 'PUBLISHED'].includes(world.status) && (
                <button
                  className={button}
                  disabled={busy}
                  onClick={() =>
                    action(async () => {
                      await apiClient.post(`${base}/${selected}/cancel`, {
                        requestId: world.requestId,
                        expectedRevision: world.revision,
                      })
                      setMessage('Creation cancelled; no customer scene was changed.')
                    })
                  }
                >
                  Cancel creation
                </button>
              )}
              <details>
                <summary>Costs and review history</summary>
                <pre className="overflow-auto whitespace-pre-wrap text-xs">
                  {JSON.stringify(
                    {
                      costs: world.evidence?.result?.costs || 'Pending / unmetered',
                      reviews: world.approvalHistory,
                      filmHandoff: world.filmHandoff,
                    },
                    null,
                    2
                  )}
                </pre>
              </details>
            </>
          ) : (
            <div className="space-y-3 py-6">
              <h2 className="text-xl">Select a scene to screen</h2>
              <p className="text-sm text-muted-foreground">
                This workspace shows rendered evidence, camera and motion phases, independent
                critique, and the interactive Debug / Surfaces / Structure viewer. It is not the
                film library.
              </p>
              <Link className="inline-block underline" to="/app/films">
                Browse rendered films →
              </Link>
            </div>
          )}
        </section>
      </div>
    </Container>
  )
}
